import { Injectable } from '@angular/core';
import { ViewStateRecord } from "@common/data-models/records/viewstate-record";
import { RecordFactory } from "@common/data-models/records/record-factory";
import { Record } from "@common/data-models/records/record";
import { ServiceRecord } from "@common/data-models/service/service-record";
import { IGenericAccessor } from "@common/interfaces/i-generic-accessor";
import { ObjectTypes } from '@common/enums/object-types';
import { ChecklistSerializer, SpecialSerializer, LookupListSerializer, LocationListSerializer, LocationListComplexSerializer, AddressSerializer, AttachmentListSerializer, StaffIDListSerializer, ViewStateFilterSerializer, ViewStateOptionsSerializer, ViewStateDelaySerializer, ViewStateSequenceSerializer, RichTextSerializer, PreviewSerializer, YoutubeSerializer, CustomFieldsSerializer, ViewStateOrderSerializer, DateTimeRangeSerializer } from '@common/data-models/serializers/serializers';
import { ListData, ListDataMetaData } from '@common/data-models/service/list-data';
import { IGlobalTablesService } from '@common/interfaces/i-global-tables-service';
import { DataType } from '@common/enums/data-type';
import { PSTFieldNames } from '@common/data-models/fields/pst-field-names';
import { KnownPropertyTypes } from "@common/enums/known-property-types";
import { CollectionRecord } from '@common/data-models/records/collection-record';
import { ApiServiceCollectionScript } from "@common/data-models/service/api-service-collection-script";
import { ApiServiceCollectionRecord } from "@common/data-models/service/api-service-collection-record";
import { ApiServiceRecord } from "@common/data-models/service/api-service-record";
import { Path } from "@common/data-models/complex-types/path";
import { DateTime } from "@common/data-models/complex-types/date-time";
import { ObjectProperties } from "@common/data-models/service/object-properties";
import { DataStore } from '@common/data-models/data-store/data-store';

@Injectable({ providedIn: 'root' })
export class PlatformConvertor {
    public globalTablesService: IGlobalTablesService;

    public useFor: { [id: string]: boolean } = {
        "logon": false,
        "search": false,
        "putTopic": false,
        "getTopic": false,
        "putNavigation": false,
        "getNavigation": false,
        "getRundown": false,
        "getDraft": false,
        "putDraft": false,
        "getRecord": false,
    };

    public fromServiceViewStateRecord<T extends ViewStateRecord>(data: any): T {
        data.instantiateChildren();
        let viewStateType = data.getRawValue("PSTViewStateType");
        let rec = RecordFactory.createViewStateForType<T>(viewStateType);
        this.deserialize(rec, data);
        return rec;
    }

    public serialize(rec: Record): { [id: string]: any } {

        let data: { [id: string]: any } = {}
        let viewStateType = 0;
        if (rec.type == ObjectTypes.PSTVIEWSTATE) {
            viewStateType = rec.getAs<number>(PSTFieldNames.PSTViewStateType);
        }

        for (let key of rec.keys) {

            let value = rec.data[key];
            if (key == PSTFieldNames.PSTExternalFiles) {
                data[key] = AttachmentListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == "Relationships") {
                continue;
            } else if (this.globalTablesService.isCheckListFieldType(key)) {
                data[key] = ChecklistSerializer.Instance.serialize(value);
            } else if (key.toLowerCase() == "guid") {
                data[key] = value;
            } else if (key == "Path") {
                data[key] = (<Path>value).fullPath;
            } else if (this.globalTablesService.isLookupListFieldType(key)) {
                data[key] = LookupListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isCustomItemFieldType(key)) {
                data[key] = CustomFieldsSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isSpecialFieldType(key)) {
                data[key] = SpecialSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isAddressFieldType(key)) {
                data[key] = AddressSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.FieldDefsDict[key] && this.globalTablesService.isStaffIDListFieldType(key)) {
                data[key] = StaffIDListSerializer.Instance(this.globalTablesService).serialize(value, key);
            } else if (key == PSTFieldNames.RSLocation) {
                data[key] = LocationListComplexSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isLocationListFieldType(key)) {
                data[key] = LocationListSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == PSTFieldNames.PSTViewStateFilter) {
                data[key] = ViewStateFilterSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (key == PSTFieldNames.PSTTopicOrder) {
                data[key] = ViewStateOrderSerializer.Instance(this.globalTablesService).serialize(value);
            } else if (this.globalTablesService.isDateRangeFieldType(key)) {
                data[key] = DateTimeRangeSerializer.Instance().serialize(value);
            } else if (value instanceof DateTime) {
                data[key] = value.moment.toISOString();
            } else if (
                key == PSTFieldNames.PSTViewStateGrouping ||
                key == PSTFieldNames.PSTViewStateSorting ||
                key == PSTFieldNames.PSTViewStateLayout) {
                data[key] = ViewStateOptionsSerializer.Instance(this.globalTablesService).serialize(value, key, viewStateType);
            } else if (key == PSTFieldNames.PSTViewStateSlideshowSequence) {
                data[key] = ViewStateSequenceSerializer.Instance.serialize(value);
            } else if (key == PSTFieldNames.PSTViewStateSlideshowDelay) {
                data[key] = ViewStateDelaySerializer.Instance.serialize(value);
            } else {
                data[key] = value;
            }
        }

        return data;
    }

    public deserialize(rec: Record, data: IGenericAccessor, cachedMetaData?: { [id: string]: number }) {
        let objectType = data.getRawValue("Type");
        let viewStateType = 0;
        if ((<number>objectType) == ObjectTypes.PSTVIEWSTATE) {
            viewStateType = data.getRawValue(PSTFieldNames.PSTViewStateType);
        }
        let metaData = cachedMetaData ? cachedMetaData : data.getMetaData();

        for (let key in metaData) {
            let fullPath = DataType.fromRaw(data.getRawValue("Path"), DataType.STRING);
            if (fullPath && (fullPath.toString().indexOf("\\L\\") > -1)) { // template
                if (
                    (key != "ParentCollectionGuid") &&
                    (key != "Database") &&
                    (!rec.fieldDefsDict[key]) &&
                    (!ListDataMetaData[key])
                ) {
                    continue;
                }
            }

            if (key == PSTFieldNames.PSTExternalFiles) {
                rec.set(key, AttachmentListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key == "Relationships") {
                continue;
            } else if (this.globalTablesService.isCheckListFieldType(key)) {
                rec.set(key, ChecklistSerializer.Instance.deserialize(data, key, objectType));
            } else if (key.toLowerCase() == "Guid") {
                rec.guid = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
            } else if (key == "Path") {
                let fullPath = DataType.fromRaw(data.getRawValue(key), DataType.STRING);
                rec.fullPath = fullPath;
            } else if (this.globalTablesService.isLookupListFieldType(key)) {
                rec.set(key, LookupListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isCustomItemFieldType(key)) {
                rec.set(key, CustomFieldsSerializer.Instance(this.globalTablesService).deserialize(data, key));
            } else if (this.globalTablesService.isSpecialFieldType(key)) {
                rec.set(key, SpecialSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isAddressFieldType(key)) {
                rec.set(key, AddressSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.FieldDefsDict[key] && this.globalTablesService.isStaffIDListFieldType(key)) {
                rec.set(key, StaffIDListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key == PSTFieldNames.RSLocation) {
                rec.set(key, LocationListComplexSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (this.globalTablesService.isLocationListFieldType(key)) {
                rec.set(key, LocationListSerializer.Instance(this.globalTablesService).deserialize(data, key, objectType));
            } else if (key == PSTFieldNames.PSTViewStateFilter) {
                rec.set(key, ViewStateFilterSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType));
            } else if (key == PSTFieldNames.PSTTopicOrder) {
                rec.set(key, ViewStateOrderSerializer.Instance(this.globalTablesService).deserialize(data));
            } else if (this.globalTablesService.isDateRangeFieldType(key)) {
                rec.set(key, DateTimeRangeSerializer.Instance().deserialize(data));
            } else if (key == "text") {
                if (objectType == ObjectTypes.WIRE) {
                    let preview = PreviewSerializer.Instance.deserialize(data, key, objectType);
                    rec.set(PSTFieldNames.PSTWireImage, preview);
                    let youtube = YoutubeSerializer.Instance.deserialize(data, key, objectType);
                    rec.set(PSTFieldNames.PSTWireVideo, youtube);
                }
                rec.set("text", RichTextSerializer.Instance.deserialize(data, key, objectType));
            } else if (key == PSTFieldNames.PSTLinkedRecords) {
                for (let record of data.getRawValue(key)) {

                    let tmpRec = this.fromServiceRecord(record);
                    DataStore.set(tmpRec);
                    rec.links.push(DataStore.byGuid(tmpRec.guid, ""));
                }
            } else if (
                key == PSTFieldNames.PSTViewStateGrouping ||
                key == PSTFieldNames.PSTViewStateSorting ||
                key == PSTFieldNames.PSTViewStateLayout) {
                rec.set(key, ViewStateOptionsSerializer.Instance(this.globalTablesService).deserialize(data, key, viewStateType));
            } else if (key == PSTFieldNames.PSTViewStateSlideshowDelay) {
                rec.set(key, ViewStateDelaySerializer.Instance.deserialize(data, key));
            } else {
                // console.log(key + " = " + data.getRawValue(key));
                // console.log(" -> " + key + " = " + DataType.fromRaw(data.getRawValue(key), metaData[key]));
                rec.set(key, DataType.fromRaw(data.getRawValue(key), metaData[key]));
            }
        }
    }

    public fromServiceRecordImpl<T extends Record>(
        data: IGenericAccessor,
        cachedMetaData?: { [id: string]: number },
        createMethod?: (number) => T
    ): T {
        let ret = <T>createMethod(data.getRawValue("Type"));
        this.deserialize(ret, data, cachedMetaData);
        return ret;
    }
    
    public fromServiceRecord<T extends Record>(data: IGenericAccessor, cachedMetaData?: { [id: string]: number }): T {
        return this.fromServiceRecordImpl<T>(data, cachedMetaData, RecordFactory.createRecordForType);
    }

    private fromServiceCollectionRecordImpl<T extends CollectionRecord>(data: ApiServiceCollectionRecord, createMethod: (number) => T): T {
        let timeStart = new Date().getTime();
        let ret = <T>createMethod(data.getRawValue("Type"));
        //data.DefaultLayout = Object.assign(new DefaultLayout, data.DefaultLayout);
        let layoutTypeMaps = {};
        if (data.DefaultLayout) {
            for (let col of data.DefaultLayout.DefaultLayout) {
                layoutTypeMaps[col.ColumnName] = KnownPropertyTypes.forValidationType(col.ColumnValidation);
            }
        }
        KnownPropertyTypes.setFor(layoutTypeMaps);
        this.deserialize(ret, data);
        let cachedMetaData: { [id: string]: number } = null;
        let children: Array<Record> = [];
        if (data.Children) {
            if (data.Children) {
                for (let i = 0; i < data.Children.length; i++) {
                    let serviceCollectionScriptData = data.Children[i];
                    let serviceCollectionScript = Object.assign(new ApiServiceCollectionScript, serviceCollectionScriptData);
                    cachedMetaData = serviceCollectionScript.getMetaData();
                    let child = this.fromServiceRecord(serviceCollectionScript, cachedMetaData);
                    children.push(child);
                }
                ret.setChildren(children);
            }
        }
        return ret;
    }

    public fromServiceCollectionRecord<T extends CollectionRecord>(data: ApiServiceCollectionRecord): T {
        return this.fromServiceCollectionRecordImpl<T>(data, RecordFactory.createRecordForType);
    }

    public fromServiceCollectionTemplateRecord<T extends CollectionRecord>(data: ApiServiceCollectionRecord): T {
        return this.fromServiceCollectionRecordImpl<T>(data, RecordFactory.createTemplateForType);
    }

    public toServiceViewStateRecord(rec: ViewStateRecord): ServiceRecord {
        let serviceRecord: ApiServiceRecord = new ApiServiceRecord();
        let data = this.serialize(rec);

        serviceRecord.Properties = data;
        serviceRecord.ObjectPropertiesCollection = new ObjectProperties(serviceRecord.Properties, true);

        return serviceRecord;
    }
}

